import { TDocumentDefinitions } from "pdfmake/interfaces";

import { intoCareDocument } from "./document";
import { DocumentContext } from "./document-context";
import { footer } from "./footer";
import { br, descriptionItem, divider, section } from "./generic";
import { observationReportHeader } from "./header";
import { linkedObservationsTable } from "./linked-observation-table";
import { residentSection } from "./resident";
import { fallToDisplay, observationToDisplay } from "./utils";
import { FallObservationReportData } from "../reports/types";

export const getFallObservationDefinition = (
	ctx: DocumentContext,
	fallObservation: FallObservationReportData,
	user?: string,
): TDocumentDefinitions =>
	intoCareDocument(ctx, {
		info: {
			title: `${ctx.t("features/pdf-generation.header.title")} - ${fallObservation.resident.lastName} ${fallObservation.resident.firstName} - ${observationToDisplay.occurredOn(
				fallObservation.occurredOn,
				ctx.locale,
			)}`,
			author: user ?? fallObservation.createdBy.label,
		},
		content: [
			observationReportHeader(ctx, fallObservation),
			divider(),
			residentSection(ctx, {
				title: ctx.t("domain-model.observation.property.resident.label"),
				resident: fallObservation.resident,
			}),
			divider(),
			section({
				title: ctx.t("features/pdf-generation.fallObservationDetails.title"),
				content: [
					{
						columnGap: 12,
						columns: [
							{
								width: "*",
								stack: descriptionItem({
									term: ctx.t("features/pdf-generation.fallObservationDetails.occurredOn.label"),
									content: observationToDisplay.occurredOn(fallObservation.occurredOn, ctx.locale),
								}),
							},
							{
								width: "*",
								stack: descriptionItem({
									term: ctx.t("features/pdf-generation.fallObservationDetails.location.label"),
									content: fallToDisplay.location(fallObservation.location, ctx.locale),
								}),
							},
							{
								width: "*",
								stack: descriptionItem({
									term: ctx.t("features/pdf-generation.fallObservationDetails.causes.label"),
									content: fallToDisplay.causes(fallObservation.causes, ctx.locale),
								}),
							},
						],
					},
					br(2),
					{
						columnGap: 12,
						columns: [
							{
								width: "*",
								stack: descriptionItem({
									term: ctx.t("features/pdf-generation.fallObservationDetails.observedOn.label"),
									content: observationToDisplay.observedOn(fallObservation.observedOn, ctx.locale),
								}),
							},
							{
								width: "*",
								stack: descriptionItem({
									term: ctx.t("features/pdf-generation.fallObservationDetails.contexts.label"),
									content: fallToDisplay.contexts(fallObservation.contexts, ctx.locale),
								}),
							},
							{
								width: "*",
								stack: descriptionItem({
									term: ctx.t("features/pdf-generation.fallObservationDetails.consequences.label"),
									content: fallToDisplay.consequences(fallObservation.consequences, ctx.locale),
								}),
							},
						],
					},
					br(2),
					{
						columnGap: 12,
						columns: [
							{
								width: "66%",
								stack: descriptionItem({
									term: ctx.t("features/pdf-generation.fallObservationDetails.notes.label"),
									content: fallObservation.notes,
								}),
							},
							{
								width: "*",
								stack: descriptionItem({
									term: ctx.t("features/pdf-generation.fallObservationDetails.correctiveActions.label"),
									content: fallToDisplay.correctiveActions(fallObservation.correctiveActions, ctx.locale),
								}),
							},
						],
					},
				],
			}),
			divider(),
			section({
				title: ctx.t("features/pdf-generation.linkedObservations.title"),
				content: fallObservation.links.length
					? linkedObservationsTable(ctx, fallObservation.links)
					: { text: ctx.t("features/pdf-generation.linkedObservations.noLinkedObservations") },
			}),
			{ text: ctx.t("features/pdf-generation.signature.label"), margin: [0, 40, 0, 0], style: "label" },
			{ text: fallObservation.createdBy.label },
		],
		footer: () => {
			return user !== undefined ? footer(ctx, user) : undefined;
		},
	});
